// TypeChips — "We should be able to filter quickly on type: intake, lease,
// renewal, EPMA etc." (§6.2).
//
// Multi-select, because "leases and renewals" and "everything owner-side" are
// both real questions. Renewal is a chip but NOT a type: a renewal is a lease
// row carrying `isRenewal` (T16), so its chip sets `renewalsOnly` and narrows
// the lease bucket rather than adding a tenth kind nobody else models.
//
// THE ACCOMMODATION CHIP IS THE ONE THAT MATTERS (C9 / §5.7). It renders only
// for a viewer on the RRA roster — which the page knows from `withheld` being
// EMPTY, never from a count. For everyone else there is no chip, no zero, no
// disabled control: an "Accommodation (0)" chip and an "Accommodation (3)"
// chip are two different disclosures, and the whole rule is that an off-roster
// viewer learns neither. They get the standing withheld line instead, which
// says the category exists and says nothing about this list.

import {
  SENT_ITEM_TYPE_GROUPS,
  SENT_ITEM_TYPE_GROUP_LABEL,
  type SentItemFacet,
  type SentItemTypeGroup,
} from '@/lib/sentItemsClient';

function countFor(facets: SentItemFacet[] | undefined, value: string): number | null {
  const hit = facets?.find((f) => f.value === value);
  return hit?.count ?? null;
}

export function TypeChips({
  selected,
  renewalsOnly,
  facets,
  showAccommodation,
  onChange,
}: {
  selected: readonly SentItemTypeGroup[];
  renewalsOnly: boolean;
  facets?: SentItemFacet[];
  /** True only when this viewer is on the RRA roster (`withheld` is empty). */
  showAccommodation: boolean;
  onChange: (next: { types: SentItemTypeGroup[]; renewalsOnly: boolean }) => void;
}): React.JSX.Element {
  const chosen = new Set(selected);

  const toggleType = (group: SentItemTypeGroup) => {
    const next = new Set(chosen);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    // Un-picking Lease takes the Renewal narrowing with it — a "renewals only"
    // filter with no lease rows in scope is a filter that matches nothing and
    // explains nothing.
    const types = SENT_ITEM_TYPE_GROUPS.filter((g) => next.has(g));
    onChange({ types, renewalsOnly: renewalsOnly && next.has('lease') });
  };

  const toggleRenewals = () => {
    const next = !renewalsOnly;
    const types = new Set(chosen);
    // Renewals live inside the lease bucket; picking the chip says so.
    if (next) types.add('lease');
    onChange({
      types: SENT_ITEM_TYPE_GROUPS.filter((g) => types.has(g)),
      renewalsOnly: next,
    });
  };

  const groups = SENT_ITEM_TYPE_GROUPS.filter(
    (g) => g !== 'accommodation' || showAccommodation,
  );

  return (
    <div className="si-chips" role="group" aria-label="Filter by type">
      {groups.map((group) => {
        const count = countFor(facets, group);
        const on = chosen.has(group);
        const chips = [
          <button
            key={group}
            type="button"
            className="pill si-chip"
            data-tone={on ? 'accent' : 'neutral'}
            aria-pressed={on}
            onClick={() => toggleType(group)}
          >
            {SENT_ITEM_TYPE_GROUP_LABEL[group]}
            {count != null && <span className="si-chip-count">{count}</span>}
          </button>,
        ];
        // Renewal sits immediately after Lease: it is a narrowing of it, and
        // putting it anywhere else in the row hides that.
        if (group === 'lease') {
          chips.push(
            <button
              key="renewal"
              type="button"
              className="pill si-chip"
              data-tone={renewalsOnly ? 'accent' : 'neutral'}
              aria-pressed={renewalsOnly}
              onClick={toggleRenewals}
            >
              Renewal
            </button>,
          );
        }
        return chips;
      })}
    </div>
  );
}
